import React, { useEffect, useMemo, useState } from "react";
import { useOutletContext } from "react-router-dom";
import {
  AlertTriangle,
  BookOpen,
  CheckCircle2,
  Clock,
  Copy,
  Download,
  ExternalLink,
  FileText,
  KeyRound,
  Lock,
  PauseCircle,
  ShieldCheck,
  Sparkles,
} from "lucide-react";
import { API_KEY_DOCS, findApiKeyDoc } from "../data/apiKeyDocs";
import { buildOemApiKeyBrand, buildOemApiKeyDocs, downloadOemAgentWorkspaceZip } from "../data/oemApiKeyDocs";
import { canViewApiKeysSurface, canViewOemApiKeysSurface } from "../utils/apiSurfaceAccess";
import { getAuthHeader } from "../utils/auth";

const API_URL = window.location.hostname === "localhost"
  ? (process.env.REACT_APP_API_URL || "http://localhost:3173")
  : window.location.origin;

const KEY_COLUMNS = [
  "名前",
  "Prefix",
  "利用権限",
  "状態",
  "最終利用",
  "操作",
];

const FEATURE_STATUS_META = {
  browser: { label: "画面操作", tone: "emerald" },
  api: { label: "API補助", tone: "indigo" },
  preview: { label: "事前確認", tone: "slate" },
  confirmation: { label: "承認必須", tone: "amber" },
  phase2: { label: "有料Gate", tone: "amber" },
};

const AGENT_FEATURE_COVERAGE = [
  {
    name: "会社概要＆NG設定",
    statuses: ["browser", "api", "preview", "confirmation"],
    summary: "会社概要、送信者情報、NGリスト、送信除外時間をAgentが確認・整形します。NG追加や設定保存は人間確認を挟みます。",
  },
  {
    name: "文面テンプレート",
    statuses: ["browser", "api", "preview", "confirmation"],
    summary: "Agentと相談しながら文面を作り、URL不足チェック、改善提案、保存前レビューを行います。保存・削除は確認付きにします。",
  },
  {
    name: "マイリスト管理",
    statuses: ["browser", "api", "preview", "confirmation"],
    summary: "ハウスリストのURL/contact URL補完、重複整理、登録、更新、削除前影響確認をAgentが支援します。登録・削除・一括変更は確認付きです。",
  },
  {
    name: "送付先リスト",
    statuses: ["browser", "api", "preview", "confirmation"],
    summary: "スコアリング済み候補を条件検索し、件数・品質・除外理由・サンプルを確認してから送付先リストを作成します。20,000件超は分割管理します。",
  },
  {
    name: "送信履歴",
    statuses: ["browser", "api"],
    summary: "人間向けの一覧画面として残し、Agentは送信結果・失敗理由・反応状況を別ロジックで集計して改善候補を返します。",
  },
  {
    name: "フォーム送信後の公開メール補足",
    statuses: ["browser", "api", "preview", "confirmation", "phase2"],
    summary: "フォーム送信で到達できなかった企業のうち、公開メール候補がある対象をAgentが整理・プレビューします。DNS/SES認証済み送信元と人間確認がある場合だけ、個別Gateで送信候補にします。",
  },
  {
    name: "案件管理",
    statuses: ["browser", "api", "preview", "confirmation"],
    summary: "見込み管理、商談後メモ、架電/アプローチ記録、次アクション、見積書作成支援をAgentが進めます。ステータス更新・受注化・NG追加・見積発行は確認付きです。",
  },
  {
    name: "顧客台帳CRM",
    statuses: ["browser", "api", "preview", "confirmation"],
    summary: "受注後の顧客情報、担当者、契約、請求/見積/領収、契約書、電子契約、対応履歴をAgentが整理します。作成・更新・送信・決済連携は確認付きです。",
  },
  {
    name: "クリックログ",
    statuses: ["browser", "api"],
    summary: "人間向けの閲覧画面として残し、Agentはクリック・Web反応・メール反応からホットリードやフォロー優先度を集計します。",
  },
  {
    name: "リード管理",
    statuses: ["browser", "api", "preview", "confirmation"],
    summary: "アクティブリード、ハウスリード、匿名ログ、AI流入、アクセス分析を整理し、案件化候補とメルマガ/シナリオの宛先設計をAgentが支援します。状態変更、案件化、CSV取込、配信対象反映は確認付きです。",
  },
  {
    name: "メール配信",
    statuses: ["browser", "api", "preview", "confirmation", "phase2"],
    summary: "AgentがHTMLメルマガ本文、件名、CTA、宛先条件、テスト送信計画を作ります。下書き保存・更新は確認付き、即時/予約配信は有料プランと実行Gateで扱います。",
  },
  {
    name: "シナリオ配信",
    statuses: ["browser", "api", "preview", "confirmation", "phase2"],
    summary: "Agentがフォーム/タグ/手動トリガー、待機時間、メールステップ、開封/クリック条件を整理します。保存・有効化・削除は確認付き、送信自動化は有料プランと実行Gateで扱います。",
  },
  {
    name: "フォーム作成",
    statuses: ["browser", "api", "preview", "confirmation"],
    summary: "Agentがフォーム項目、タグ、通知、自動返信、完了後導線を設計します。LPと連動するため、作成/更新/削除/外部タグ反映は確認付きです。",
  },
  {
    name: "LP制作・公開",
    statuses: ["browser", "api", "preview", "confirmation"],
    summary: "AgentがHTML一体型LPを作成し、フォーム連携、画像、公開URL、OGP、公開状態を確認します。CSS/JSは別欄に分けずHTML内へ集約します。",
  },
  {
    name: "資料作成",
    statuses: ["browser", "api", "preview", "confirmation"],
    summary: "Agentが資料構成を作り、1ページごとにHTMLスライドを生成します。人間は順番、文言、グラフ、画像、細部を後から編集できます。削除・並び替え・PDF出力は確認付きです。",
  },
  {
    name: "マーケティング設定",
    statuses: ["browser", "preview", "confirmation"],
    summary: "DNS設定、タグ、CAPI、計測設定、連携状態を確認します。設定変更は確認付きです。",
  },
  {
    name: "アカウント管理",
    statuses: ["browser", "api"],
    summary: "プラン、利用状況、送信準備状態を確認します。請求や契約変更は対象外です。",
  },
  {
    name: "ユーザー管理",
    statuses: ["browser", "confirmation"],
    summary: "ユーザー一覧や権限状態の確認は支援できます。招待、停止、権限変更は当面ブラウザ確認を前提にします。",
  },
];

const OPERATION_RULES = [
  ["画面操作", "既存画面をAgentが操作・確認できます。API化そのものを目的にしません。"],
  ["API補助", "専用の読み取り・確認導線がある領域は、Agentが迷わず安全に扱えます。"],
  ["事前確認", "保存・公開・一括変更の前に差分、件数、サンプル、リスクを確認します。"],
  ["承認必須", "保存、公開、削除、状態変更、一括変更は人間の承認を挟みます。"],
  ["有料Gate", "送信実行、配信開始、大量実行は有料プラン、送信枠、プレビュー、人間承認を確認してから扱います。"],
];

const LIFECYCLE_STEPS = [
  ["1", "発行前確認", "利用できるAPIとプラン条件を確認します。この時点ではまだキーを作成しません。"],
  ["2", "APIキー発行", "APIキーは作成直後に一度だけ表示します。SalesBreaker側には復元できない形で保存します。"],
  ["3", "外部ツールで利用", "ブラウザログインなしで利用できます。ただし Authorization: Bearer APIキー は必須です。"],
  ["4", "利用状況を確認", "最終利用日時、成功/失敗、利用量を確認できるようにします。"],
  ["5", "停止 / 無効化", "一時停止と永久無効化を分けて管理します。"],
];

const TABS = [
  { id: "keys", label: "APIキー管理", icon: KeyRound },
  { id: "docs", label: "Docs / Manual", icon: BookOpen },
  { id: "coverage", label: "AI連携範囲", icon: Sparkles },
];

const AGENT_WORKSPACE_ZIP_URL = `${process.env.PUBLIC_URL || "/app"}/downloads/salesbreaker-agent-workspace.zip`;
const MCP_BRIDGE_ZIP_URL = `${process.env.PUBLIC_URL || "/app"}/downloads/salesbreaker-mcp-bridge.zip`;
const CODEX_OFFICIAL_URL = "https://openai.com/ja-JP/codex/";

// セルフサービス発行は製品の正(公開ドキュメントでも案内済み)。既定で有効とし、
// 止めたい時だけ REACT_APP_API_KEY_SELF_SERVICE_ENABLED=false でビルドする。
// (旧: 未設定だと「発行準備中」バッジが出る嘘表示になっていた)
const selfServiceEnabled = process.env.REACT_APP_API_KEY_SELF_SERVICE_ENABLED !== "false";

const ISSUE_SCOPE_OPTIONS = [
  { id: "crm.read", label: "CRMデータ取得", description: "/api/v1/crm/* の送信履歴・反応データを外部CRMやBIへ連携します。" },
  { id: "quality.read", label: "リスト品質確認", description: "/api/v1/sales-list/* の企業リスト品質・スコア・推奨アクションを確認します。" },
  { id: "operator.read", label: "AIツール向け確認", description: "SalesBreakerの主要画面、送信履歴、クリック反応、AI流入・Web計測をAIが読み取り・診断できます。" },
  { id: "operator.assist", label: "AI操作補助", description: "読み取りに加えて、会社概要、NG設定、文面テンプレート、マイリスト、送付先リスト、活動ログをAIが整理・保存できます。" },
  { id: "campaign.execute", label: "送信実行（有料）", description: "送付先リスト・テンプレート・事前確認・人間承認後に送信実行します。直接検索送信は不可です。" },
];

const PERMISSION_LABELS = {
  "crm.read": "CRMデータ取得",
  "quality.read": "リスト品質確認",
  "operator.read": "AIツール向け確認",
  "operator.assist": "AI操作補助",
  "campaign.execute": "送信実行（有料）",
};

const CodeBlock = ({ children }) => (
  <pre className="overflow-x-auto rounded-lg bg-slate-950 p-4 text-xs leading-6 text-slate-100">
    <code>{children}</code>
  </pre>
);

const SurfaceCard = ({ icon: Icon, title, description, children, tone = "indigo" }) => {
  const toneClass = {
    indigo: "bg-indigo-50 text-indigo-600",
    emerald: "bg-emerald-50 text-emerald-600",
    amber: "bg-amber-50 text-amber-700",
    rose: "bg-rose-50 text-rose-700",
    slate: "bg-slate-100 text-slate-700",
  }[tone] || "bg-indigo-50 text-indigo-600";

  return (
    <section className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
      <div className="flex items-start gap-3">
        <div className={`rounded-lg p-2 ${toneClass}`}>
          <Icon size={20} />
        </div>
        <div>
          <h2 className="text-lg font-bold text-slate-900">{title}</h2>
          {description && <p className="mt-1 text-sm leading-6 text-slate-600">{description}</p>}
        </div>
      </div>
      <div className="mt-5">{children}</div>
    </section>
  );
};

const StatusPill = ({ children, tone = "slate" }) => {
  const classes = {
    emerald: "border-emerald-200 bg-emerald-50 text-emerald-700",
    amber: "border-amber-200 bg-amber-50 text-amber-800",
    rose: "border-rose-200 bg-rose-50 text-rose-700",
    indigo: "border-indigo-200 bg-indigo-50 text-indigo-700",
    slate: "border-slate-200 bg-slate-50 text-slate-700",
  }[tone];

  return (
    <span className={`inline-flex items-center rounded-full border px-2.5 py-1 text-xs font-bold ${classes}`}>
      {children}
    </span>
  );
};

const downloadMarkdown = (doc) => {
  const blob = new Blob([doc.content], { type: "text/markdown;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = doc.filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const statusTone = (status) => {
  if (status === "active") return "emerald";
  if (status === "disabled") return "amber";
  if (status === "revoked") return "rose";
  return "slate";
};

const statusLabel = (status) => {
  if (status === "active") return "利用中";
  if (status === "disabled") return "一時停止";
  if (status === "revoked") return "無効化済み";
  if (status === "archived") return "削除済み";
  return status || "不明";
};

const permissionLabel = (permission) => PERMISSION_LABELS[permission] || permission;

const formatDateTime = (value) => {
  if (!value) return "未使用";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString("ja-JP", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

function ApiKeysPage({ surface = "saas" }) {
  const { user, oemBranding } = useOutletContext() || {};
  const isOemSurface = surface === "oem";
  const oemBrand = useMemo(
    () => buildOemApiKeyBrand({ oemBranding, user }),
    [oemBranding, user]
  );
  const productName = isOemSurface ? oemBrand.productName : "SalesBreaker";
  const apiProductName = isOemSurface ? oemBrand.apiProductName : "SalesBreaker API";
  const workspaceDisplayName = isOemSurface
    ? oemBrand.workspaceDisplayName
    : "JAPAN GTM Agent Workspace powered by SalesBreaker";
  const apiKeySurfaceCopy = isOemSurface
    ? {
        statusPill: "クライアントAPIキー",
        heroDescription: `${workspaceDisplayName}を使って、${productName}配下のクライアントがAI Agentや外部ツールに作業文脈を渡すための案内ページです。API基盤は共通で使い、DocsとWorkspace ZIPは${productName}ブランドで案内します。`,
        tenantTitle: "対象クライアントID",
        tenantHelp: "APIキーはここに表示されているクライアントに紐づきます。OEM本部用ではなく、OEM傘下クライアントのAI連携・CRM連携用キーです。",
        issueStatusWhenDisabled: "管理者確認付き",
        managementDescription: `対象クライアントがAI Agent、CRM、BIツールから${productName}の情報を安全に扱うためのキーを管理します。`,
        storageNotReadyHelp: "APIキー発行は管理者確認付きで提供中です。対象クライアントを確認してから発行してください。",
        emptyDescription: "対象クライアントのAPIキーを発行すると、作成直後に一度だけキーの実値が表示されます。無料プランは情報取得・確認が中心で、送信実行系は有料プランと承認付きGateで扱います。",
        issueDescription: "対象クライアントのAPIキーを発行します。キーの実値は作成直後に一度だけ表示されます。送信実行は有料プラン、送付先リスト、事前確認、人間承認が必要です。",
        tenantPreviewLabel: "対象クライアントID",
      }
    : {
        statusPill: "APIキー",
        heroDescription: `${workspaceDisplayName}を使って、CodexなどのAI Agentが${productName}の各画面を迷わず安全に扱うための案内ページです。ブラウザ操作で触れる領域、APIで補助できる領域、人間確認が必要な操作を分けて整理しています。`,
        tenantTitle: "利用中の会社ID",
        tenantHelp: "",
        issueStatusWhenDisabled: "発行準備中",
        managementDescription: `外部CRM、BIツール、AIエージェントから${productName}の情報を取得するためのキーを管理します。`,
        storageNotReadyHelp: "APIキーの保存機能がまだ準備中です。管理者確認後に発行できます。",
        emptyDescription: "APIキーを発行すると、作成直後に一度だけキーの実値が表示されます。無料プランは情報取得・確認が中心で、送信実行系は有料プランと承認付きGateで扱います。",
        issueDescription: "APIキーの実値は作成直後に一度だけ表示されます。送信実行は有料プラン、送付先リスト、事前確認、人間承認が必要です。",
        tenantPreviewLabel: "会社ID",
      };
  const canView = isOemSurface ? canViewOemApiKeysSurface(user) : canViewApiKeysSurface(user);
  const selectedClientId = useMemo(() => {
    const role = user?.role;
    if (role === "admin" || role === "admin_staff") {
      return localStorage.getItem("selectedClientId") || user?.client_id || "";
    }
    return user?.client_id || "";
  }, [user]);
  const [activeTab, setActiveTab] = useState("keys");
  const [selectedDocId, setSelectedDocId] = useState("agents-md");
  const [copyState, setCopyState] = useState("");
  const [keys, setKeys] = useState([]);
  const [keysLoading, setKeysLoading] = useState(false);
  const [keyError, setKeyError] = useState("");
  const [isIssueOpen, setIsIssueOpen] = useState(false);
  const [issueForm, setIssueForm] = useState({
    name: "",
    description: "",
    scope_bundles: ["crm.read"],
    raw_key_display_once: false,
    store_key_securely: false,
  });
  const [issuePreview, setIssuePreview] = useState(null);
  const [createdSecret, setCreatedSecret] = useState(null);
  const [issueLoading, setIssueLoading] = useState(false);

  const docs = useMemo(
    () => (isOemSurface ? buildOemApiKeyDocs(oemBrand) : API_KEY_DOCS),
    [isOemSurface, oemBrand]
  );
  const selectedDoc = useMemo(() => {
    if (!isOemSurface) return findApiKeyDoc(selectedDocId);
    return docs.find((doc) => doc.id === selectedDocId) || docs[0];
  }, [docs, isOemSurface, selectedDocId]);
  const orderedDocs = useMemo(() => {
    return [...docs].sort((a, b) => {
      if (a.id === "agents-md") return -1;
      if (b.id === "agents-md") return 1;
      if (a.id === "start-here") return -1;
      if (b.id === "start-here") return 1;
      if (a.id === "agent-contract-pack") return -1;
      if (b.id === "agent-contract-pack") return 1;
      if (a.id === "agent-operating-manual") return -1;
      if (b.id === "agent-operating-manual") return 1;
      return 0;
    });
  }, [docs]);
  const tenantLabel = selectedClientId || user?.client_id || "client not selected";

  const WorkspaceZipControl = ({ className = "", children }) => {
    if (isOemSurface) {
      return (
        <button
          type="button"
          onClick={() => downloadOemAgentWorkspaceZip(oemBrand, docs)}
          className={className}
        >
          {children}
        </button>
      );
    }
    return (
      <a href={AGENT_WORKSPACE_ZIP_URL} download className={className}>
        {children}
      </a>
    );
  };

  const brandCopy = (value) => {
    if (!isOemSurface) return value;
    return String(value || "")
      .replace(/SalesBreaker/g, productName)
      .replace(/JAPAN GTM Agent Contract Pack/g, `${productName} Agent Contract Pack`)
      .replace(/JAPAN GTM Agent Workspace/g, workspaceDisplayName);
  };

  const apiKeyHeaders = (json = false) => {
    const headers = { ...getAuthHeader(json) };
    if (selectedClientId && (user?.role === "admin" || user?.role === "admin_staff")) {
      headers["x-salesbreaker-client-id"] = selectedClientId;
    }
    return headers;
  };

  const fetchKeys = async () => {
    if (!canView) return;
    if (!selectedClientId) {
      setKeys([]);
      setKeyError("api_key_client_not_selected");
      return;
    }
    setKeysLoading(true);
    setKeyError("");
    try {
      const res = await fetch(`${API_URL}/api/developer/api-keys?limit=50`, {
        headers: apiKeyHeaders(),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.ok === false) {
        setKeys([]);
        setKeyError(data.error?.code || data.error || "api_key_list_failed");
        return;
      }
      setKeys(data.data?.keys || []);
    } catch (error) {
      setKeys([]);
      setKeyError("api_key_list_network_error");
    } finally {
      setKeysLoading(false);
    }
  };

  useEffect(() => {
    fetchKeys();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canView, selectedClientId]);

  const toggleIssueScope = (scope) => {
    setIssueForm((current) => {
      const hasScope = current.scope_bundles.includes(scope);
      const nextScopes = hasScope
        ? current.scope_bundles.filter((item) => item !== scope)
        : [...current.scope_bundles, scope];
      return {
        ...current,
        scope_bundles: nextScopes.length ? nextScopes : current.scope_bundles,
      };
    });
    setIssuePreview(null);
    setCreatedSecret(null);
  };

  const previewIssue = async () => {
    setIssueLoading(true);
    setKeyError("");
    setCreatedSecret(null);
    try {
      const res = await fetch(`${API_URL}/api/developer/api-keys/preview`, {
        method: "POST",
        headers: apiKeyHeaders(true),
        body: JSON.stringify({
          name: issueForm.name,
          description: issueForm.description,
          scope_bundles: issueForm.scope_bundles,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.ok === false) {
        setIssuePreview(null);
        setKeyError(data.error?.code || data.error || "api_key_preview_failed");
        return;
      }
      setIssuePreview(data.data?.create_preview
        ? { ...data.data.create_preview, plan_gate: data.data.plan_gate || null }
        : null);
    } catch (error) {
      setIssuePreview(null);
      setKeyError("api_key_preview_network_error");
    } finally {
      setIssueLoading(false);
    }
  };

  const createIssue = async () => {
    setIssueLoading(true);
    setKeyError("");
    setCreatedSecret(null);
    try {
      const res = await fetch(`${API_URL}/api/developer/api-keys`, {
        method: "POST",
        headers: apiKeyHeaders(true),
        body: JSON.stringify({
          name: issueForm.name,
          description: issueForm.description,
          scope_bundles: issueForm.scope_bundles,
          acknowledgements: {
            raw_key_display_once: issueForm.raw_key_display_once,
            store_key_securely: issueForm.store_key_securely,
          },
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.ok === false) {
        setCreatedSecret(null);
        setKeyError(data.error?.code || data.error || "api_key_create_failed");
        return;
      }
      setCreatedSecret(data.data?.secret || null);
      setIssuePreview(null);
      await fetchKeys();
    } catch (error) {
      setCreatedSecret(null);
      setKeyError("api_key_create_network_error");
    } finally {
      setIssueLoading(false);
    }
  };

  const changeKeyStatus = async (publicId, action) => {
    setKeyError("");
    try {
      const res = await fetch(`${API_URL}/api/developer/api-keys/${encodeURIComponent(publicId)}/${action}`, {
        method: "POST",
        headers: apiKeyHeaders(true),
        body: JSON.stringify({ reason: `${action} from API key screen` }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.ok === false) {
        setKeyError(data.error?.code || data.error || `api_key_${action}_failed`);
        return;
      }
      await fetchKeys();
    } catch (error) {
      setKeyError(`api_key_${action}_network_error`);
    }
  };

  const copySelectedDoc = async () => {
    try {
      await navigator.clipboard.writeText(selectedDoc.content);
      setCopyState(`copied:${selectedDoc.id}`);
      window.setTimeout(() => setCopyState(""), 1800);
    } catch (error) {
      setCopyState("copy_failed");
      window.setTimeout(() => setCopyState(""), 1800);
    }
  };

  if (!canView) {
    return (
      <div className="mx-auto max-w-3xl rounded-lg border border-amber-200 bg-amber-50 p-6 text-amber-900">
          <div className="flex items-center gap-3">
            <Lock size={24} />
          <h1 className="text-xl font-bold">APIキー画面は限定表示中です</h1>
        </div>
        <p className="mt-3 text-sm leading-6">
          表示権限のあるユーザーだけが、この画面から{apiProductName}キーとAI連携Docsを確認できます。
        </p>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-7xl space-y-6">
      <div className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
        <div className="flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <div className="flex flex-wrap gap-2">
              <StatusPill tone="indigo">{apiKeySurfaceCopy.statusPill}</StatusPill>
              <StatusPill tone="amber">AI連携Docs</StatusPill>
              <StatusPill tone="emerald">ログインなしAPI</StatusPill>
              <StatusPill tone="emerald">Agent対応</StatusPill>
              <StatusPill tone="slate">{isOemSurface ? "OEM Workspace ZIP対応" : "JAPAN GTM Workspace ZIPあり"}</StatusPill>
            </div>
            <h1 className="mt-4 text-3xl font-bold tracking-tight text-slate-950">APIキー / AI連携</h1>
            <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-600">
              {apiKeySurfaceCopy.heroDescription}
            </p>
          </div>
          <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700">
            <div className="font-bold text-slate-900">{apiKeySurfaceCopy.tenantTitle}</div>
            <div className="mt-1 font-mono text-xs">{tenantLabel}</div>
            {apiKeySurfaceCopy.tenantHelp && (
              <div className="mt-2 max-w-xs text-xs leading-5 text-slate-500">{apiKeySurfaceCopy.tenantHelp}</div>
            )}
            <div className="mt-3 font-bold text-slate-900">発行機能</div>
            <div className="mt-1">
              {selfServiceEnabled ? (
                <StatusPill tone="emerald">利用可能</StatusPill>
              ) : (
                <StatusPill tone="amber">{apiKeySurfaceCopy.issueStatusWhenDisabled}</StatusPill>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white p-2 shadow-sm">
        <div className="flex min-w-max gap-2">
          {TABS.map(({ id, label, icon: Icon }) => {
            const active = activeTab === id;
            return (
              <button
                key={id}
                type="button"
                onClick={() => setActiveTab(id)}
                className={`inline-flex items-center gap-2 rounded-md px-4 py-2 text-sm font-bold transition ${
                  active
                    ? "bg-slate-950 text-white"
                    : "text-slate-600 hover:bg-slate-100 hover:text-slate-950"
                }`}
              >
                <Icon size={16} />
                {label}
              </button>
            );
          })}
        </div>
      </div>

      {activeTab === "coverage" && (
        <div className="space-y-6">
          <SurfaceCard
            icon={Sparkles}
            title={`AIが操作できる${productName}機能`}
            description={`${productName}の画面・業務機能ごとに、Agentの操作方法と確認ルールを整理しています。`}
            tone="indigo"
          >
            <div className="mb-4 rounded-lg border border-emerald-200 bg-emerald-50 p-4 text-sm leading-6 text-emerald-900">
              <div className="font-bold">API化そのものが目的ではありません</div>
              <div className="mt-1">
                既にブラウザ操作で触れる機能はそのまま使い、専用導線がある領域はAPIで補助します。
                保存・公開・削除・一括変更は人間の承認付きで進めます。
              </div>
            </div>
            <div className="mb-4 grid gap-3 sm:grid-cols-3">
              <div className="rounded-lg border border-slate-200 bg-white p-4">
                <div className="text-xs font-bold uppercase tracking-wide text-slate-500">Coverage</div>
                <div className="mt-2 text-sm font-bold text-slate-950">Agentが迷わず操作するための早見表</div>
                <p className="mt-2 text-xs leading-5 text-slate-600">
                  画面ごとに、ブラウザ操作で良いもの、API補助があるもの、確認が必要なものを分けます。
                </p>
              </div>
              <div className="rounded-lg border border-slate-200 bg-white p-4">
                <div className="text-xs font-bold uppercase tracking-wide text-slate-500">Contracts</div>
                <div className="mt-2 text-sm font-bold text-slate-950">詳細JSONはDocsへ集約</div>
                <p className="mt-2 text-xs leading-5 text-slate-600">
                  画面には巨大なJSONを出さず、Agent向けの入出力仕様はMarkdown側で管理します。
                </p>
              </div>
              <div className="rounded-lg border border-slate-200 bg-white p-4">
                <div className="text-xs font-bold uppercase tracking-wide text-slate-500">Send</div>
                <div className="mt-2 text-sm font-bold text-slate-950">送信は確認付き</div>
                <p className="mt-2 text-xs leading-5 text-slate-600">
                  送信実行は送付先リスト、プレビュー、人間確認、有料プラン/送信枠確認を前提に扱います。直接送信は不可です。
                </p>
              </div>
            </div>
            <div className="grid gap-3 lg:grid-cols-2">
              {AGENT_FEATURE_COVERAGE.map((feature) => (
                <div key={feature.name} className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                    <div className="text-sm font-bold text-slate-950">{feature.name}</div>
                    <div className="flex flex-wrap gap-1.5">
                      {feature.statuses.map((status) => {
                        const meta = FEATURE_STATUS_META[status];
                        return (
                          <StatusPill key={status} tone={meta.tone}>
                            {meta.label}
                          </StatusPill>
                        );
                      })}
                    </div>
                  </div>
                  <p className="mt-2 text-xs leading-5 text-slate-600">{feature.summary}</p>
                </div>
              ))}
            </div>
          </SurfaceCard>

          <div className="grid gap-6 xl:grid-cols-[1.1fr_0.9fr]">
            <SurfaceCard
              icon={ShieldCheck}
              title="安全な操作ルール"
              description="ブラウザ操作とAPI補助を併用し、保存・公開・削除・一括変更は人間確認を挟みます。"
              tone="emerald"
            >
              <div className="grid gap-3 sm:grid-cols-5">
                {OPERATION_RULES.map(([title, text]) => (
                  <div key={title} className="rounded-lg border border-slate-200 bg-white p-3">
                    <div className="text-sm font-bold text-slate-950">{title}</div>
                    <p className="mt-2 text-xs leading-5 text-slate-600">{text}</p>
                  </div>
                ))}
              </div>
              <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm leading-6 text-amber-900">
                <div className="font-bold">送信実行は確認付きで扱います</div>
                <div className="mt-1">
                  Agentは送付先リスト、文面テンプレート、送信件数、送信枠、完了見込みを確認し、人間承認後に実行します。プレビューなしの直接送信は行いません。
                </div>
              </div>
            </SurfaceCard>

            <SurfaceCard
              icon={CheckCircle2}
              title="運用方針"
              description={`人間は${apiProductName}キーを管理し、AIやエンジニアはDocs / Manualと${workspaceDisplayName}を読みます。`}
              tone="amber"
            >
              <ul className="space-y-2 text-sm leading-6 text-slate-700">
                <li>APIキー管理はそのまま利用できます。</li>
                <li>画面の主役は「{productName}機能カバレッジ」です。</li>
                <li>画面操作とAPI補助を両方前提にします。</li>
                <li>送信実行は有料プランと承認付きGateで扱います。</li>
                <li>AI向けの詳細仕様はDocs / Manualへ集約します。</li>
                <li>ローカルで使う{workspaceDisplayName} ZIPを用意します。</li>
              </ul>
            </SurfaceCard>
          </div>
        </div>
      )}

      {activeTab === "keys" && (
        <div className="space-y-6">
          <div className="grid gap-6 xl:grid-cols-[1.2fr_0.8fr]">
            <SurfaceCard
              icon={KeyRound}
              title="APIキー管理"
              description={apiKeySurfaceCopy.managementDescription}
            >
              <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="text-sm leading-6 text-slate-600">
                  発行済みAPIキーの実値は再表示できません。作成直後の一度だけ表示される値を安全な場所に保存してください。
                  APIキーを使うとブラウザにログインしなくても対象クライアントのデータへアクセスできますが、認証なし公開APIではありません。
                </div>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={fetchKeys}
                    className="inline-flex items-center gap-2 rounded-md border border-slate-300 px-3 py-2 text-sm font-bold text-slate-700 hover:bg-slate-50"
                  >
                    <Clock size={16} />
                    更新
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setIsIssueOpen(true);
                      setKeyError("");
                      setIssuePreview(null);
                      setCreatedSecret(null);
                    }}
                    className="inline-flex items-center gap-2 rounded-md bg-slate-950 px-3 py-2 text-sm font-bold text-white hover:bg-slate-800"
                  >
                    <KeyRound size={16} />
                    APIキーを発行
                  </button>
                </div>
              </div>
              {keyError && (
                <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
                  <div className="font-bold">APIキー管理 route からの応答</div>
                  <div className="mt-1 font-mono text-xs">{keyError}</div>
                  {keyError === "api_key_storage_not_ready" && (
                    <div className="mt-2 text-xs leading-5">
                      {apiKeySurfaceCopy.storageNotReadyHelp}
                    </div>
                  )}
                </div>
              )}
              <div className="overflow-hidden rounded-lg border border-slate-200">
                <table className="min-w-full divide-y divide-slate-200 text-sm">
                  <thead className="bg-slate-50 text-left text-xs font-bold uppercase tracking-wide text-slate-500">
                    <tr>
                      {KEY_COLUMNS.map((label) => (
                        <th key={label} className="px-4 py-3">{label}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 bg-white">
                    {keysLoading && (
                      <tr>
                        <td colSpan={KEY_COLUMNS.length} className="px-4 py-8 text-center text-sm text-slate-600">
                          APIキーを読み込み中です。
                        </td>
                      </tr>
                    )}
                    {!keysLoading && keys.length === 0 && (
                      <tr>
                        <td colSpan={KEY_COLUMNS.length} className="px-4 py-10 text-center">
                          <div className="mx-auto max-w-xl">
                            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-lg bg-slate-100 text-slate-600">
                              <KeyRound size={24} />
                            </div>
                            <h3 className="mt-4 text-base font-bold text-slate-900">APIキーはまだありません</h3>
                            <p className="mt-2 text-sm leading-6 text-slate-600">
                              {apiKeySurfaceCopy.emptyDescription}
                            </p>
                            <button
                              type="button"
                              onClick={() => setIsIssueOpen(true)}
                              className="mt-4 inline-flex items-center gap-2 rounded-md bg-slate-950 px-4 py-2 text-sm font-bold text-white hover:bg-slate-800"
                            >
                              <KeyRound size={16} />
                              APIキーを発行
                            </button>
                          </div>
                        </td>
                      </tr>
                    )}
                    {!keysLoading && keys.map((key) => (
                      <tr key={key.public_id} className="align-top">
                        <td className="px-4 py-4">
                          <div className="font-bold text-slate-900">{key.name}</div>
                          {key.description && (
                            <div className="mt-1 max-w-xs text-xs leading-5 text-slate-500">{key.description}</div>
                          )}
                          <div className="mt-2 font-mono text-xs text-slate-500">{key.public_id}</div>
                        </td>
                        <td className="px-4 py-4">
                          <div className="font-mono text-xs text-slate-800">{key.key_prefix}...</div>
                          <div className="mt-1 text-xs text-slate-500">last4: {key.key_last4}</div>
                        </td>
                        <td className="px-4 py-4">
                          <div className="flex max-w-xs flex-wrap gap-1.5">
                            {(key.scope_bundles || []).map((scope) => (
                              <StatusPill key={scope} tone="indigo">{permissionLabel(scope)}</StatusPill>
                            ))}
                          </div>
                        </td>
                        <td className="px-4 py-4">
                          <StatusPill tone={statusTone(key.status)}>{statusLabel(key.status)}</StatusPill>
                        </td>
                        <td className="px-4 py-4 text-xs leading-5 text-slate-600">
                          {formatDateTime(key.last_used_at)}
                        </td>
                        <td className="px-4 py-4">
                          <div className="flex flex-wrap gap-2">
                            <button
                              type="button"
                              disabled={!key.can_disable}
                              onClick={() => changeKeyStatus(key.public_id, "disable")}
                              className={`inline-flex items-center gap-1 rounded-md border px-2.5 py-1.5 text-xs font-bold ${
                                key.can_disable
                                  ? "border-amber-300 text-amber-800 hover:bg-amber-50"
                                  : "cursor-not-allowed border-slate-200 text-slate-400"
                              }`}
                            >
                              <PauseCircle size={14} />
                              停止
                            </button>
                            <button
                              type="button"
                              disabled={!key.can_revoke}
                              onClick={() => changeKeyStatus(key.public_id, "revoke")}
                              className={`inline-flex items-center gap-1 rounded-md border px-2.5 py-1.5 text-xs font-bold ${
                                key.can_revoke
                                  ? "border-rose-300 text-rose-700 hover:bg-rose-50"
                                  : "cursor-not-allowed border-slate-200 text-slate-400"
                              }`}
                            >
                              <AlertTriangle size={14} />
                              無効化
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </SurfaceCard>

            <SurfaceCard
              icon={ShieldCheck}
              title="APIキーの流れ"
              description="発行前確認から停止・無効化まで、この画面で管理します。"
              tone="emerald"
            >
              <div className="space-y-3">
                {LIFECYCLE_STEPS.map(([number, title, text]) => (
                  <div key={number} className="flex gap-3 rounded-lg border border-slate-200 bg-slate-50 p-3">
                    <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-slate-950 text-xs font-bold text-white">
                      {number}
                    </div>
                    <div>
                      <div className="text-sm font-bold text-slate-900">{title}</div>
                      <p className="mt-1 text-xs leading-5 text-slate-600">{brandCopy(text)}</p>
                    </div>
                  </div>
                ))}
              </div>
            </SurfaceCard>
          </div>

          <div className="grid gap-6">
            <SurfaceCard
              icon={CheckCircle2}
              title="無料 / 有料の境界"
              description="無料プランでは情報確認を中心に、有料プランでは送信枠、プレビュー、人間承認、監査ログを通した実行系を扱います。"
              tone="amber"
            >
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4">
                  <div className="font-bold text-emerald-900">無料候補</div>
                  <ul className="mt-2 space-y-1 text-sm text-emerald-800">
                    <li>CRMデータ取得</li>
                    <li>企業リスト品質確認</li>
                    <li>AIツール向け情報確認</li>
                    <li>送信前プレビュー</li>
                  </ul>
                </div>
                <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
                  <div className="font-bold text-amber-900">有料 + 承認付き実行</div>
                  <ul className="mt-2 space-y-1 text-sm text-amber-800">
                    <li>AIエージェントによる確認付き実行</li>
                    <li>フォーム送信の承認付き実行</li>
                    <li>メール配信の承認付き実行</li>
                    <li>シナリオ配信の承認付き実行</li>
                  </ul>
                </div>
              </div>
            </SurfaceCard>
          </div>
        </div>
      )}

      {activeTab === "docs" && (
        <div className="grid gap-6 xl:grid-cols-[320px_1fr]">
          <SurfaceCard
            icon={BookOpen}
            title="Docs / Agent Workspace"
            description={`AIやエンジニアが読むMarkdownをここに集約します。ローカル作業では${workspaceDisplayName} ZIPの利用を推奨します。`}
          >
            <div className="mb-4 rounded-lg border border-slate-200 bg-slate-50 p-4">
              <div className="flex items-start gap-3">
                <div className="rounded-lg bg-white p-2 text-slate-700 shadow-sm">
                  <Sparkles size={18} />
                </div>
                <div>
                  <div className="text-sm font-bold text-slate-950">Codexを使う準備</div>
                  <p className="mt-1 text-xs leading-5 text-slate-600">
                    Codexをまだ使ったことがない場合は、まずOpenAI公式ページで概要を確認してください。
                    {productName}側では、下のAgent Workspace ZIPを解凍してCodexに読み込ませることで、操作ルールとAPI契約を渡せます。
                  </p>
                </div>
              </div>
              <div className="mt-3 flex flex-col gap-2 sm:flex-row">
                <a
                  href={CODEX_OFFICIAL_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 rounded-md border border-slate-300 bg-white px-3 py-2 text-sm font-bold text-slate-800 hover:bg-slate-100"
                >
                  <ExternalLink size={16} />
                  Codexとは？（OpenAI公式）
                </a>
                <WorkspaceZipControl className="inline-flex items-center justify-center gap-2 rounded-md bg-slate-950 px-3 py-2 text-sm font-bold text-white hover:bg-slate-800">
                  <Download size={16} />
                  Agent Workspace ZIPをダウンロード
                </WorkspaceZipControl>
              </div>
            </div>
            <WorkspaceZipControl className="mb-3 flex w-full items-start gap-3 rounded-lg border border-indigo-200 bg-indigo-50 p-3 text-left text-indigo-900 transition hover:bg-indigo-100">
              <Download className="mt-0.5 shrink-0" size={18} />
              <span>
                <span className="block text-sm font-bold">{workspaceDisplayName} ZIP</span>
                <span className="mt-1 block text-xs leading-5">
                  解凍したフォルダをCodex / Claude Codeに読ませるためのAGENTS.md、Manual、JSON契約一式です。
                </span>
              </span>
            </WorkspaceZipControl>
            <div className="mb-4 rounded-lg border border-slate-200 bg-slate-50 p-3">
              <div className="text-sm font-bold text-slate-900">Agentに任せる手順</div>
              <ol className="mt-2 space-y-1 text-xs leading-5 text-slate-600">
                <li>1. {workspaceDisplayName} ZIPをダウンロードして解凍</li>
                <li>2. 解凍したフォルダをCodex / Claude Codeに読ませる</li>
                <li>3. 「会社概要を整えて」「テンプレ作って」「結果分析して」など、やりたいことを伝える</li>
              </ol>
            </div>
            <div className="space-y-2">
              {orderedDocs.map((doc) => (
                <button
                  key={doc.id}
                  type="button"
                  onClick={() => setSelectedDocId(doc.id)}
                  className={`w-full rounded-md border px-3 py-2 text-left transition ${
                    selectedDocId === doc.id
                      ? "border-slate-950 bg-slate-950 text-white"
                      : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
                  }`}
                >
                  <div className="text-sm font-bold">{doc.title}</div>
                  <div className={`mt-1 text-xs ${selectedDocId === doc.id ? "text-slate-200" : "text-slate-500"}`}>
                    {doc.summary}
                  </div>
                </button>
              ))}
            </div>
          </SurfaceCard>

          <SurfaceCard
            icon={FileText}
            title={selectedDoc.title}
            description={`${selectedDoc.filename} / ${selectedDoc.audience}`}
            tone="slate"
          >
            <div className="mb-4 flex flex-wrap gap-2">
              <button
                type="button"
                onClick={copySelectedDoc}
                className="inline-flex items-center gap-2 rounded-md border border-slate-300 px-3 py-2 text-sm font-bold text-slate-700 hover:bg-slate-50"
              >
                <Copy size={16} />
                {copyState === `copied:${selectedDoc.id}` ? "コピー済み" : "Markdownをコピー"}
              </button>
              <button
                type="button"
                onClick={() => downloadMarkdown(selectedDoc)}
                className="inline-flex items-center gap-2 rounded-md bg-slate-950 px-3 py-2 text-sm font-bold text-white hover:bg-slate-800"
              >
                <Download size={16} />
                Markdownをダウンロード
              </button>
            </div>
            <CodeBlock>{selectedDoc.content}</CodeBlock>
          </SurfaceCard>
        </div>
      )}

      {isIssueOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/50 p-4">
          <div className="max-h-[92vh] w-full max-w-3xl overflow-y-auto rounded-lg bg-white shadow-xl">
            <div className="border-b border-slate-200 p-5">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h2 className="text-xl font-bold text-slate-950">APIキーを発行</h2>
                  <p className="mt-1 text-sm leading-6 text-slate-600">
                    {apiKeySurfaceCopy.issueDescription}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setIsIssueOpen(false)}
                  className="rounded-md border border-slate-300 px-3 py-2 text-sm font-bold text-slate-700 hover:bg-slate-50"
                >
                  閉じる
                </button>
              </div>
            </div>
            <div className="space-y-5 p-5">
              <div className="grid gap-4 sm:grid-cols-2">
                <label className="block">
                  <span className="text-sm font-bold text-slate-900">キー名</span>
                  <input
                    value={issueForm.name}
                    onChange={(event) => {
                      setIssueForm((current) => ({ ...current, name: event.target.value }));
                      setIssuePreview(null);
                      setCreatedSecret(null);
                    }}
                    className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-slate-950 focus:outline-none"
                    placeholder="例: Codex確認用キー"
                  />
                </label>
                <label className="block">
                  <span className="text-sm font-bold text-slate-900">説明</span>
                  <input
                    value={issueForm.description}
                    onChange={(event) => {
                      setIssueForm((current) => ({ ...current, description: event.target.value }));
                      setIssuePreview(null);
                      setCreatedSecret(null);
                    }}
                    className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-slate-950 focus:outline-none"
                    placeholder="用途メモ"
                  />
                </label>
              </div>

              <div>
                <div className="text-sm font-bold text-slate-900">利用権限</div>
                <p className="mt-1 text-xs leading-5 text-slate-500">
                  APIキーは選択した権限だけを利用できます。Operator APIを使うAgentには「AIツール向け確認」、リスト品質APIには「リスト品質確認」を付けてください。APIキーrouteはブラウザログインなしで使えますが、必ず Authorization: Bearer APIキー で認証します。{brandCopy("JAPAN GTM Agent Contract Pack")}の `api_key_ready` / `browser_jwt_required` に従って、APIキーrouteとログイン済みブラウザ操作を使い分けます。
                </p>
                <div className="mt-2 grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
                  {ISSUE_SCOPE_OPTIONS.map((scope) => {
                    const checked = issueForm.scope_bundles.includes(scope.id);
                    return (
                      <button
                        key={scope.id}
                        type="button"
                        onClick={() => toggleIssueScope(scope.id)}
                        className={`rounded-lg border p-3 text-left transition ${
                          checked
                            ? "border-slate-950 bg-slate-950 text-white"
                            : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
                        }`}
                      >
                        <div className="text-sm font-bold">{scope.label}</div>
                        <div className={`mt-1 text-xs leading-5 ${checked ? "text-slate-200" : "text-slate-500"}`}>
                          {brandCopy(scope.description)}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="rounded-lg border border-indigo-200 bg-indigo-50 p-4 text-sm leading-6 text-indigo-950">
                <div className="flex items-start gap-3">
                  <Sparkles size={18} className="mt-1 shrink-0" />
                  <div>
                    <div className="font-bold">Agentへ渡す前の必須確認</div>
                    <p className="mt-1 text-indigo-900">
                      Codex / Claude CodeなどにAPIキーを渡す前に、必ず最新の{workspaceDisplayName} ZIPをダウンロードし、
                      解凍したフォルダ内の <span className="font-mono text-xs">START_HERE.md</span> と{" "}
                      <span className="font-mono text-xs">workspace-version.json</span> を確認させてください。
                      古いWorkspaceのまま{productName}を操作させないでください。
                    </p>
                    <WorkspaceZipControl className="mt-3 inline-flex items-center gap-2 rounded-md bg-indigo-700 px-3 py-2 text-xs font-bold text-white hover:bg-indigo-800">
                      <Download size={14} />
                      {workspaceDisplayName} ZIPをダウンロード
                    </WorkspaceZipControl>
                    {!isOemSurface && (
                      <div className="mt-4 border-t border-indigo-200 pt-3">
                        <div className="font-bold">Claude Code / Codex はMCPブリッジ接続が最短です</div>
                        <p className="mt-1 text-indigo-900">
                          MCPブリッジ(依存ゼロ・Node 18+)を使うと、エージェントが{productName}のAPIを
                          構造化ツールとして直接呼べます。ZIPを解凍し、同梱READMEの
                          <span className="font-mono text-xs"> claude mcp add </span>
                          (Codexはconfig.toml)手順にAPIキーを設定してください。
                        </p>
                        <a
                          href={MCP_BRIDGE_ZIP_URL}
                          download
                          className="mt-3 inline-flex items-center gap-2 rounded-md bg-slate-900 px-3 py-2 text-xs font-bold text-white hover:bg-slate-700"
                        >
                          <Download size={14} />
                          MCPブリッジ ZIPをダウンロード(Claude Code / Codex用)
                        </a>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
                <div className="font-bold text-amber-950">一度だけ表示されるAPIキーの確認</div>
                <label className="mt-3 flex items-start gap-2 text-sm leading-6 text-amber-900">
                  <input
                    type="checkbox"
                    checked={issueForm.raw_key_display_once}
                    onChange={(event) => setIssueForm((current) => ({ ...current, raw_key_display_once: event.target.checked }))}
                    className="mt-1"
                  />
                  APIキーの実値は作成直後に一度だけ表示され、一覧・詳細画面では再表示されないことを理解しました。
                </label>
                <label className="mt-2 flex items-start gap-2 text-sm leading-6 text-amber-900">
                  <input
                    type="checkbox"
                    checked={issueForm.store_key_securely}
                    onChange={(event) => setIssueForm((current) => ({ ...current, store_key_securely: event.target.checked }))}
                    className="mt-1"
                  />
                  表示されたAPIキーを安全な場所に保存し、URL、公開リポジトリ、フロントエンドコード、AI AgentのログやMarkdownに貼り付けません。
                </label>
              </div>

              {keyError && (
                <div className="rounded-lg border border-rose-200 bg-rose-50 p-3 text-sm text-rose-800">
                  <div className="font-bold">発行エラー</div>
                  <div className="mt-1 font-mono text-xs">{keyError}</div>
                </div>
              )}

              {issuePreview && (
                <div className="rounded-lg border border-indigo-200 bg-indigo-50 p-4">
                  <div className="font-bold text-indigo-950">発行前確認</div>
                  <div className="mt-2 grid gap-2 text-sm text-indigo-900 sm:grid-cols-2">
                    <div>利用権限: {(issuePreview.scope_bundles || []).map(permissionLabel).join(" / ")}</div>
                    <div>対象API: {issuePreview.api_families?.join(", ")}</div>
                    <div>一度だけ表示: {issuePreview.display_once_required ? "はい" : "いいえ"}</div>
                    <div>{apiKeySurfaceCopy.tenantPreviewLabel}: APIキーから自動判定</div>
                    <div>Plan gate: {issuePreview.plan_gate?.required_plan || "free"}</div>
                  </div>
                </div>
              )}

              {createdSecret?.raw_key && (
                <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4">
                  <div className="font-bold text-emerald-950">APIキーを発行しました。これは一度だけ表示されます。</div>
                  <div className="mt-3 rounded-md bg-white p-3 font-mono text-xs text-slate-900">
                    {createdSecret.raw_key}
                  </div>
                  <button
                    type="button"
                    onClick={() => navigator.clipboard.writeText(createdSecret.raw_key)}
                    className="mt-3 inline-flex items-center gap-2 rounded-md bg-emerald-700 px-3 py-2 text-sm font-bold text-white hover:bg-emerald-800"
                  >
                    <Copy size={16} />
                    APIキーをコピー
                  </button>
                  <div className="mt-4 rounded-md border border-emerald-200 bg-white p-3 text-sm leading-6 text-emerald-950">
                    <div className="font-bold">次にAgentへ渡すもの</div>
                    <p className="mt-1">
                      APIキーだけを渡さず、最新の{workspaceDisplayName} ZIPも渡してください。Agentには{" "}
                      <span className="font-mono text-xs">START_HERE.md</span> と{" "}
                      <span className="font-mono text-xs">workspace-version.json</span> を先に読ませ、
                      画面操作・API操作の契約が最新であることを確認させてから作業を開始してください。
                    </p>
                    <WorkspaceZipControl className="mt-3 inline-flex items-center gap-2 rounded-md border border-emerald-300 px-3 py-2 text-xs font-bold text-emerald-800 hover:bg-emerald-50">
                      <Download size={14} />
                      {workspaceDisplayName} ZIPをダウンロード
                    </WorkspaceZipControl>
                  </div>
                </div>
              )}

              <div className="flex flex-wrap justify-end gap-2 border-t border-slate-200 pt-4">
                <button
                  type="button"
                  onClick={previewIssue}
                  disabled={issueLoading || !issueForm.name}
                  className="inline-flex items-center gap-2 rounded-md border border-slate-300 px-4 py-2 text-sm font-bold text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:text-slate-400"
                >
                  <ShieldCheck size={16} />
                  発行前確認
                </button>
                <button
                  type="button"
                  onClick={createIssue}
                  disabled={
                    issueLoading ||
                    !issueForm.name ||
                    !issueForm.raw_key_display_once ||
                    !issueForm.store_key_securely
                  }
                  className="inline-flex items-center gap-2 rounded-md bg-slate-950 px-4 py-2 text-sm font-bold text-white hover:bg-slate-800 disabled:cursor-not-allowed disabled:bg-slate-300"
                >
                  <KeyRound size={16} />
                  {issueLoading ? "処理中" : "発行する"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ApiKeysPage;
